/*
 * wh-journey-layout-fix.js
 * WorkoutHealthy — Journey (Mediavine) in-content ad corrections.
 *
 * Module 1 — stray box relocator. 2026-08-11, master plan pre-publish gate item 1.
 * Module 2 — intro guard.         2026-08-30, ad placement audit fix 1.
 * Module 3 — ad labels.           2026-08-30, ad placement audit fix 3.
 *
 * Measurements and rationale: project doc claude/in-article-ad-placement-audit-aug30.md
 * Loaded from layout/theme.liquid on blog and article templates, never on reviews.
 */

/* MODULE 1 — stray box relocator.
 * Journey can resolve its in-content target to `.blog-layout` and inject `div.mv-ad-box`
 * as a sibling of `.blog-layout__main`. The theme rule
 *   .blog-layout > div:not(.blog-layout__main):not(.blog-layout__sidebar){flex:1 0 100%}
 * then forces it full width, the flex row wraps, and the sidebar drops ~24,400px down the
 * page. Fix: move each injected box into the article body at a paragraph boundary.
 * Idempotent — a moved box is tagged, so our own writes never re-feed the observer. */
(function () {
  'use strict';

  var MOVED = 'data-wh-relocated';
  var STRAY = '.blog-layout > .mv-ad-box:not([' + MOVED + '])';

  function articleBody() {
    var main = document.querySelector('.blog-layout__main');
    if (!main) return null;
    return (
      main.querySelector('.element-text--rte') ||
      main.querySelector('.rte') ||
      main.querySelector('.article__body') ||
      main
    );
  }

  // Top-level blocks an ad can sit in front of without splitting a table or a figure.
  function anchors(host) {
    return Array.prototype.filter.call(host.children, function (el) {
      return /^(P|H2|H3|UL|OL)$/.test(el.tagName);
    });
  }

  function relocate() {
    var strays = document.querySelectorAll(STRAY);
    if (!strays.length) return;

    var host = articleBody();
    if (!host) return;

    var spots = anchors(host);

    Array.prototype.forEach.call(strays, function (box) {
      box.setAttribute(MOVED, '1');
      var placed = host.querySelectorAll('[' + MOVED + ']').length;
      var idx = Math.floor(spots.length * (0.4 + 0.25 * placed));
      if (spots.length && idx < spots.length) host.insertBefore(box, spots[idx]);
      else host.appendChild(box);
    });
  }

  function start() {
    var layout = document.querySelector('.blog-layout');
    if (!layout) return;
    relocate();
    new MutationObserver(relocate).observe(layout, { childList: true });
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start);
  else start();
})();

/* MODULE 2 — intro guard.
 * Journey placed its first in-content unit between the affiliate disclosure and paragraph
 * one — 43 words in, as an interscroller, in the slot a lead image would hold. Mediavine's
 * wrapper never places an ad between elements inside the same <div> (help.mediavine.com,
 * "How to Manually Block Ads in your Content"), so the intro gets wrapped in one: top-level
 * nodes are collected until at least 80 words of body copy have passed AND the next heading
 * is reached, capped at 8 nodes. Word count rather than paragraph count, because a guide
 * whose intro is one paragraph then an H2 would otherwise swallow half the article.
 *
 * The theme's rich-text spacing is a descendant rule, so wrapped paragraphs keep it. The one
 * casualty is `.element-text.element-text--rte p:last-child{margin-bottom:0}` — the last
 * intro paragraph becomes a last child of the wrapper and loses its gap. Module 3's CSS
 * restores it.
 *
 * This file is deferred, so it runs before DOMContentLoaded and the wrapper exists by the
 * time snippets/wh-affiliate-disclosure.liquid looks for it. The observer is the fallback:
 * if Journey wins the race, any box landing inside the intro is moved out below it. */
(function () {
  'use strict';

  var MIN_WORDS = 80; // body copy a reader gets before the first in-content ad
  var MAX_NODES = 8; // hard stop, so a heading-light article is never swallowed whole
  var MIN_LEN = 40; // ignore spacers like <p>&nbsp;</p>

  function body() {
    return document.querySelector('.journey-content');
  }

  function wrapper(host) {
    var first = host.firstElementChild;
    return first && first.classList.contains('wh-intro-block') ? first : null;
  }

  function wrapIntro() {
    var host = body();
    if (!host || wrapper(host)) return;

    var kids = Array.prototype.slice.call(host.children);
    if (kids.length < 6) return; // too short to be worth splitting

    var intro = [];
    var strays = [];
    var words = 0;

    for (var i = 0; i < kids.length; i++) {
      var el = kids[i];
      if (el.classList.contains('mv-ad-box')) {
        strays.push(el); // never wrap an ad
        continue;
      }
      // Only break on a heading: an ad between a heading and its first paragraph is worse
      // than one a little further down.
      if (/^H[1-6]$/.test(el.tagName) && words >= MIN_WORDS) break;
      intro.push(el);
      if (el.tagName === 'P') {
        var text = (el.textContent || '').trim();
        if (text.length >= MIN_LEN) words += text.split(/\s+/).length;
      }
      if (intro.length >= MAX_NODES) break;
    }

    if (!intro.length || !words) return;

    var wrap = document.createElement('div');
    wrap.className = 'wh-intro-block';
    host.insertBefore(wrap, intro[0]);
    intro.forEach(function (el) {
      wrap.appendChild(el);
    });
    strays.forEach(function (box) {
      host.insertBefore(box, wrap.nextSibling);
    });
  }

  // Journey wins the race sometimes: pull any box that landed inside the intro out below it.
  function evictLateBoxes(host, wrap) {
    Array.prototype.forEach.call(wrap.querySelectorAll('.mv-ad-box'), function (box) {
      host.insertBefore(box, wrap.nextSibling);
    });
  }

  // snippets/wh-affiliate-disclosure.liquid picks the container holding the most paragraphs
  // and inserts above its first one. The intro wrapper changes which container that is, so
  // the disclosure lands below the intro instead of above it. Seat it back at the top.
  // The observer runs as a microtask, before paint, so nothing flashes.
  function reseatDisclosure(host, wrap) {
    var disc = host.querySelector('.wh-disclosure');
    if (!disc || disc.parentNode === wrap) return;
    wrap.insertBefore(disc, wrap.firstChild);
  }

  function tidy() {
    var host = body();
    if (!host) return;
    var wrap = wrapper(host);
    if (!wrap) return;
    evictLateBoxes(host, wrap);
    reseatDisclosure(host, wrap);
  }

  function start() {
    var host = body();
    if (!host) return;
    wrapIntro();
    new MutationObserver(tidy).observe(host, { childList: true, subtree: true });
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start);
  else start();
})();

/* MODULE 3 — in-content ad labels.
 * The units carried no label and no separator, at the same width as the body copy, so a
 * display ad read as part of the article. Each one now gets an "Advertisement" caption and
 * a hairline rule above it.
 *
 * The ad box is never touched: the caption is a sibling inserted before it, so no ad node
 * is moved, re-parented or restyled and no filled iframe reloads. Styling the box directly
 * is not an option — Journey sets height:350px, overflow:clip !important and display:flex
 * inline on `.mv-ad-box`, so a ::before would be clipped and would lay out beside the ad.
 * The CSS ships from here so the caption can never render unstyled. */
(function () {
  'use strict';

  var TAGGED = 'data-wh-labelled';
  var STYLE_ID = 'wh-ad-label-css';
  var CSS =
    '.wh-ad-label{display:block;margin:26px 0 4px;padding-top:10px;' +
    'border-top:1px solid rgba(0,0,0,.12);' +
    'font-family:"Open Sans",-apple-system,BlinkMacSystemFont,"Segoe UI",Roboto,Helvetica,Arial,sans-serif;' +
    'font-size:10px;line-height:1.2;letter-spacing:.14em;text-transform:uppercase;' +
    'text-align:center;color:#8a929c}' +
    '.wh-intro-block{margin:0}' +
    '.wh-intro-block > p:last-child{margin-bottom:var(--size-8) !important}';

  function injectCss() {
    if (document.getElementById(STYLE_ID)) return;
    var style = document.createElement('style');
    style.id = STYLE_ID;
    style.textContent = CSS;
    document.head.appendChild(style);
  }

  function caption() {
    var el = document.createElement('div');
    el.className = 'wh-ad-label';
    el.setAttribute('aria-hidden', 'true');
    el.textContent = 'Advertisement';
    return el;
  }

  function decorate() {
    var host = document.querySelector('.journey-content');
    if (!host) return;
    var boxes = host.querySelectorAll('.mv-ad-box:not([' + TAGGED + '])');
    Array.prototype.forEach.call(boxes, function (box) {
      box.setAttribute(TAGGED, '1'); // tag first, so a throw cannot double-caption
      if (box.parentNode) box.parentNode.insertBefore(caption(), box);
    });
  }

  function start() {
    var host = document.querySelector('.journey-content');
    if (!host) return;
    injectCss();
    decorate();
    new MutationObserver(decorate).observe(host, { childList: true, subtree: true });
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start);
  else start();
})();
